import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import MarketingCopyEditor from '../components/admin/MarketingCopyEditor';
import SEOHead from '../components/SEOHead';
import { isPushSupported, getPushPermission, subscribeToPush, syncPushSubscription, registerServiceWorker, getBrowserSubscriptionEndpoint, getServerPushStatus, nuclearReset, getSwVersion, getSupportedContentEncodings } from '../utils/pushNotifications';
import OverviewTab from '../components/admin/OverviewTab';
import AppointmentsTab from '../components/admin/AppointmentsTab';
import ReviewsTab from '../components/admin/ReviewsTab';
import MessagesTab from '../components/admin/MessagesTab';
import CustomersTab from '../components/admin/CustomersTab';
import SettingsTab from '../components/admin/SettingsTab';
import ServicesTab from '../components/admin/ServicesTab';
import PromotionsTab from '../components/admin/PromotionsTab';
import CampaignsTab from '../components/admin/CampaignsTab';
import CustomerImportTab from '../components/admin/CustomerImportTab';
import RemindersTab from '../components/admin/RemindersTab';
import ReportsTab from '../components/admin/ReportsTab';

const API = process.env.REACT_APP_API_URL || '/api';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [appointments, setAppointments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ today: 0, pending: 0, customers: 0, revenue: 0, avgRating: 0, totalReviews: 0 });
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState({ google_places_api_key: '', google_place_id: '', mechanics_count: '6', slot_interval_minutes: '15', enable_notification_sound: 'true', enable_sms_notifications: 'false', admin_notification_phone: '', twilio_account_sid: '', twilio_auth_token: '', twilio_from_number: '', enable_email_notifications: 'false', email_from_address: '', smtp_host: '', smtp_port: '', smtp_user: '', smtp_password: '', email_provider: 'smtp', gmail_client_id: '', gmail_client_secret: '', gmail_refresh_token: '', enable_customer_sms_notifications: 'false', shop_timezone: 'America/Toronto', shop_name: '', shop_address: '', shop_phone: '', shop_phone2: '', translation_provider: 'none', translation_api_key: '' });

  const [noteModal, setNoteModal] = useState({ show: false, notes: '', customer: '' });
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState(null);

  const [pushStatus, setPushStatus] = useState('loading');
  const [pushError, setPushError] = useState(null);
  const [pushDebug, setPushDebug] = useState(null);
  const [showPushDebug, setShowPushDebug] = useState(false);

  const [toasts, setToasts] = useState([]);

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectDelayRef = useRef(1000);

  const playNotificationSound = useCallback(() => {
    if (settings.enable_notification_sound !== 'true') return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.value = 880;
      gain1.gain.setValueAtTime(0.3, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.2);
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.value = 1108.73;
      gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + 0.15);
      osc2.stop(ctx.currentTime + 0.4);
    } catch (e) { /* autoplay policy */ }
  }, [settings.enable_notification_sound]);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`);
      setNotifications(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [apptRes, statsRes, reviewRes, msgRes] = await Promise.allSettled([
        axios.get(`${API}/admin/appointments`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/reviews/all`),
        axios.get(`${API}/contact`)
      ]);

      // Unwrap paginated responses: endpoints may return { data: [...], pagination } or plain arrays
      const unwrap = (val) => Array.isArray(val) ? val : (Array.isArray(val?.data) ? val.data : []);
      const appts = apptRes.status === 'fulfilled' ? unwrap(apptRes.value.data) : [];
      const serverStats = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
      const revs = reviewRes.status === 'fulfilled' ? unwrap(reviewRes.value.data) : [];
      const msgs = msgRes.status === 'fulfilled' ? unwrap(msgRes.value.data) : [];

      if (apptRes.status === 'fulfilled') setAppointments(appts);
      if (reviewRes.status === 'fulfilled') setReviews(revs);
      if (msgRes.status === 'fulfilled') setMessages(msgs);

      const today = new Date().toISOString().split('T')[0];
      const todayAppts = appts.filter(a => a.appointment_date === today);
      const pending = appts.filter(a => a.status === 'pending');
      const revenue = appts
          .filter(a => a.status === 'completed')
          .reduce((sum, a) => sum + (a.price_min || 0), 0);
      const avgRating = revs.length > 0
          ? (revs.reduce((sum, r) => sum + r.rating, 0) / revs.length).toFixed(1)
          : 0;

      setStats({
        today: todayAppts.length,
        pending: pending.length,
        customers: serverStats.total_customers || 0,
        revenue: revenue,
        avgRating: avgRating,
        totalReviews: revs.length
      });
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addToast = useCallback((notification) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...notification, toastId: id }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.toastId !== id));
    }, 8000);
  }, []);

  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(t => t.toastId !== toastId));
  };

  const handleToastClick = useCallback(async (toast) => {
    removeToast(toast.toastId);
    try {
      await axios.patch(`${API}/admin/notifications/${toast.id}/read`);
      loadNotifications();
    } catch (e) { /* ignore */ }
    const metadata = typeof toast.metadata === 'string' ? JSON.parse(toast.metadata) : toast.metadata;
    const appointmentId = metadata?.appointment_id;
    if (appointmentId) {
      setActiveTab('appointments');
      setHighlightedAppointmentId(appointmentId);
      setTimeout(() => {
        const row = document.getElementById(`appt-${appointmentId}`);
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      setTimeout(() => setHighlightedAppointmentId(null), 3300);
    }
  }, [loadNotifications]);

  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('cjpa_token');
    if (!token) return;
    const baseUrl = API.replace('/api', '');
    const sseUrl = `${baseUrl}/api/admin/notifications/stream?token=${encodeURIComponent(token)}`;
    try {
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;
      es.addEventListener('connected', () => {
        reconnectDelayRef.current = 1000;
      });
      es.addEventListener('new_notification', (event) => {
        try {
          const notification = JSON.parse(event.data);
          addToast(notification);
          playNotificationSound();
          loadData();
          loadNotifications();
          if (Notification.permission === 'granted') {
            try {
              const osNotif = new Notification('CJPA - New Appointment', {
                body: notification.message || 'New appointment booked',
                icon: '/logo.webp',
                tag: 'cjpa-sse-' + Date.now(),
                requireInteraction: true
              });
              osNotif.onclick = () => { window.focus(); osNotif.close(); };
            } catch (notifErr) {
              if (navigator.serviceWorker?.controller) {
                navigator.serviceWorker.controller.postMessage({
                  type: 'SHOW_NOTIFICATION',
                  title: 'CJPA - New Appointment',
                  body: notification.message || 'New appointment booked',
                  url: '/admin'
                });
              }
            }
          }
        } catch (e) { /* ignore parse errors */ }
      });
      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;
        const delay = reconnectDelayRef.current;
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(delay * 2, 30000);
          connectSSE();
        }, delay);
      };
    } catch (e) { /* fallback */ }
  }, [addToast, playNotificationSound, loadData, loadNotifications]);

  useEffect(() => {
    loadData();
    loadNotifications();
    connectSSE();
    const pollInterval = setInterval(() => {
      loadData();
      loadNotifications();
    }, 15000);
    return () => {
      clearInterval(pollInterval);
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [loadData, loadNotifications, connectSSE]);

  const refreshPushStatus = useCallback(async () => {
    try {
      if (!isPushSupported()) { setPushStatus('unsupported'); return; }
      registerServiceWorker().catch(() => {});
      const perm = getPushPermission();
      if (perm === 'denied') { setPushStatus('denied'); return; }
      const token = localStorage.getItem('cjpa_token');
      const [browserEndpoint, serverStatus, swVersion] = await Promise.all([
        getBrowserSubscriptionEndpoint(),
        token ? getServerPushStatus(token) : Promise.resolve({ count: 0, subscriptions: [] }),
        getSwVersion()
      ]);
      setPushDebug({
        browserEndpoint: browserEndpoint ? browserEndpoint.slice(0, 40) + '...' : 'none',
        serverCount: serverStatus.count,
        serverEndpoints: serverStatus.subscriptions.map(s => s.endpoint_prefix),
        match: browserEndpoint && serverStatus.count > 0,
        swVersion: swVersion || 'none',
        encodings: getSupportedContentEncodings() || ['unknown']
      });
      if (perm === 'granted' && browserEndpoint) {
        if (token) { syncPushSubscription(token, false).catch(() => {}); }
        if (serverStatus.count > 0) {
          setPushStatus('subscribed');
        } else {
          setPushError('not_stored_on_server');
          setPushStatus('error');
        }
      } else {
        setPushStatus('default');
      }
    } catch {
      setPushStatus('default');
    }
  }, []);

  useEffect(() => {
    refreshPushStatus();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'PUSH_RECEIVED') {
          console.log('[Page] SW confirmed push received:', event.data.data);
        }
      });
    }
  }, [refreshPushStatus]);

  const handleEnablePush = async () => {
    setPushStatus('loading');
    setPushError(null);
    try {
      const token = localStorage.getItem('cjpa_token');
      const result = await subscribeToPush(token, false);
      if (result.success) {
        await refreshPushStatus();
      } else if (result.reason === 'permission_denied') {
        setPushStatus('denied');
      } else {
        setPushError(result.reason);
        setPushStatus('error');
      }
    } catch (err) {
      setPushError(err.message);
      setPushStatus('error');
    }
  };

  const handleResyncPush = async () => {
    const token = localStorage.getItem('cjpa_token');
    if (!token) return;
    setPushStatus('loading');
    const result = await syncPushSubscription(token, false);
    console.log('[Push] Resync result:', result);
    await refreshPushStatus();
  };

  const handleNuclearReset = async () => {
    if (!window.confirm('This will fully unregister the service worker, delete all push subscriptions, and resubscribe fresh. Continue?')) return;
    setPushStatus('loading');
    setPushError(null);
    const token = localStorage.getItem('cjpa_token');
    const result = await nuclearReset(token);
    console.log('[Push] Nuclear reset result:', result);
    if (result.success) {
      alert('Reset complete! Steps: ' + result.steps.join(' → '));
    } else {
      alert('Reset failed: ' + result.reason + '\nSteps: ' + result.steps.join(' → '));
    }
    await refreshPushStatus();
  };

  const handleTestPush = async (mode = 'payload') => {
    try {
      const token = localStorage.getItem('cjpa_token');
      const res = await axios.post(`${API}/push/test`, { mode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      alert('[' + mode + '] ' + (data.success ? 'Sent OK' : 'Failed') + ' (' + data.status + ')\n' + JSON.stringify(data, null, 2));
    } catch (err) {
      alert('Test push failed: ' + err.message);
    }
  };

  const handleCheckBeacons = async () => {
    try {
      const token = localStorage.getItem('cjpa_token');
      const res = await axios.get(`${API}/push/beacons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      if (data.beacons?.length > 0) {
        alert('Push beacons received:\n' + JSON.stringify(data.beacons, null, 2));
      } else {
        alert('No beacons received yet. This means the SW push event has NOT fired from any FCM push.');
      }
    } catch (err) {
      alert('Check beacons failed: ' + err.message);
    }
  };

  const handleSwNotify = async () => {
    if (!navigator.serviceWorker?.controller) {
      alert('No active service worker controller. Try refreshing the page.');
      return;
    }
    navigator.serviceWorker.controller.postMessage({ type: 'SHOW_NOTIFICATION' });
    alert('Sent SHOW_NOTIFICATION to SW. You should see an OS notification now.');
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.patch(`${API}/admin/notifications/read-all`);
      loadNotifications();
    } catch (err) { /* ignore */ }
  };

  // RBAC: determine which tabs are visible based on user role
  // IMPORTANT: This must be ABOVE the loading early-return to avoid violating Rules of Hooks
  const role = user?.role || 'staff';
  const staffTabs = ['overview', 'appointments', 'reviews', 'messages', 'customers', 'reminders'];
  const managerTabs = [...staffTabs, 'services', 'promotions', 'campaigns', 'reports'];
  const adminTabs = [...managerTabs, 'settings', 'import', 'marketing'];

  const allowedTabKeys = role === 'admin' ? adminTabs
    : role === 'manager' ? managerTabs
    : staffTabs;

  // RBAC: redirect to overview if user navigates to a tab they can't access
  useEffect(() => {
    if (!allowedTabKeys.includes(activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab, role]);

  useEffect(() => {
    if (activeTab === 'overview') loadData();
  }, [activeTab]);

  const formatDateTime = (date) => new Date(date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0C1220' }}>
        <div style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem', letterSpacing: '0.08em' }}>
          {t.common.loading}
        </div>
      </div>
  );

  const statCardStyle = {
    background: '#111827',
    border: '1px solid rgba(148,163,184,0.2)',
    padding: '2rem',
    textAlign: 'center'
  };

  const allTabs = [
    ['overview', t.admin.tabs.overview],
    ['appointments', t.admin.tabs.appointments],
    ['reviews', t.admin.tabs.reviews],
    ['messages', t.admin.tabs.messages],
    ['customers', t.admin.tabs.customers],
    ['services', t.admin.tabs.services],
    ['promotions', t.admin.tabs.promotions],
    ['campaigns', t.admin.tabs.campaigns || 'Campaigns'],
    ['reminders', t.admin.tabs?.reminders || 'Reminders'],
    ['reports', t.admin.tabs?.reports || 'Reports'],
    ['settings', t.admin.tabs.settings],
    ['import', t.admin.tabs?.import || 'Import'],
    ['marketing', t.admin.tabs.marketing],
  ];

  const tabs = allTabs.filter(([key]) => allowedTabKeys.includes(key));

  const renderActiveTab = () => {
    // Guard: don't render a tab the user doesn't have access to
    if (!allowedTabKeys.includes(activeTab)) return null;
    switch (activeTab) {
      case 'overview':
        return <OverviewTab stats={stats} appointments={appointments} reviews={reviews} messages={messages} t={t} />;
      case 'appointments':
        return <AppointmentsTab noteModal={noteModal} setNoteModal={setNoteModal} highlightedAppointmentId={highlightedAppointmentId} t={t} />;
      case 'reviews':
        return <ReviewsTab t={t} />;
      case 'messages':
        return <MessagesTab t={t} formatDateTime={formatDateTime} addToast={addToast} />;
      case 'customers':
        return <CustomersTab t={t} loadData={loadData} />;
      case 'reminders':
        return <RemindersTab t={t} />;
      case 'settings':
        return <SettingsTab settings={settings} setSettings={setSettings} t={t} />;
      case 'services':
        return <ServicesTab settings={settings} t={t} />;
      case 'promotions':
        return <PromotionsTab settings={settings} t={t} />;
      case 'campaigns':
        return <CampaignsTab settings={settings} t={t} />;
      case 'reports':
        return <ReportsTab />;
      case 'import':
        return <CustomerImportTab t={t} />;
      case 'marketing':
        return <MarketingCopyEditor />;
      default:
        return null;
    }
  };

  return (
      <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
        <SEOHead page="admin" />
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(1rem, 2vw, 2rem)' }}>

          {/* Header with Notification Bell */}
          <div style={{ marginBottom: '3rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '3rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '0.5rem' }}>
                {t.admin.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.admin.titleHighlight}</span>
              </h1>
              <p style={{ color: '#94A3B8', fontSize: '0.9rem' }}>{t.admin.welcome}, {user?.first_name}</p>
            </div>

            {/* Notification Bell + Push Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {pushStatus === 'default' && (
                <button
                  onClick={handleEnablePush}
                  style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.3)', color: '#E8ECF0', padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '0.7rem', letterSpacing: '0.05em' }}
                  aria-label={t.admin.push?.enable || 'Enable push notifications'}
                >
                  {t.admin.push?.enableShort || 'Enable Push'}
                </button>
              )}
              {pushStatus === 'subscribed' && (
                <>
                  <span style={{ color: '#70d070', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    &#10003; Push
                  </span>
                  <button onClick={() => handleTestPush('payload')} style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.3)', color: '#94A3B8', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                    Test
                  </button>
                  <button onClick={() => setShowPushDebug(!showPushDebug)} style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.2)', color: '#64748B', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                    Debug
                  </button>
                </>
              )}
              {pushStatus === 'error' && (
                <>
                  <button onClick={handleResyncPush} style={{ background: 'transparent', border: '1px solid #D42B2B', color: '#D42B2B', padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '0.65rem' }} title={pushError}>
                    Resync Push
                  </button>
                  <button onClick={() => setShowPushDebug(!showPushDebug)} style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.2)', color: '#64748B', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                    Debug
                  </button>
                </>
              )}
              {pushStatus === 'denied' && (
                <span style={{ color: '#D42B2B', fontSize: '0.65rem' }} title={t.admin.push?.denied || 'Push notifications blocked in browser settings'}>
                  {t.admin.push?.blockedShort || 'Push blocked'}
                </span>
              )}
              {showPushDebug && pushDebug && (
                <div style={{ width: '100%', background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(148,163,184,0.2)', padding: '0.6rem', fontSize: '0.65rem', fontFamily: 'monospace', color: '#94A3B8', marginTop: '0.3rem' }}>
                  <div>SW Version: {pushDebug.swVersion}</div>
                  <div>Browser: {pushDebug.browserEndpoint}</div>
                  <div>Server subs: {pushDebug.serverCount}</div>
                  {pushDebug.serverEndpoints?.map((ep, i) => <div key={i}>  Server[{i}]: {ep}</div>)}
                  <div style={{ color: pushDebug.match ? '#70d070' : '#D42B2B' }}>
                    Match: {pushDebug.match ? 'YES' : 'NO'}
                  </div>
                  <div>Encodings: {pushDebug.encodings?.join(', ')}</div>
                  {pushError && <div style={{ color: '#D42B2B' }}>Error: {pushError}</div>}
                  <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={() => handleTestPush('payload')} style={{ background: 'transparent', border: '1px solid #70d070', color: '#70d070', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      Test: Payload
                    </button>
                    <button onClick={() => handleTestPush('empty')} style={{ background: 'transparent', border: '1px solid #f0c040', color: '#f0c040', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      Test: Empty
                    </button>
                    <button onClick={() => handleTestPush('aesgcm')} style={{ background: 'transparent', border: '1px solid #60a0e0', color: '#60a0e0', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      Test: aesgcm
                    </button>
                  </div>
                  <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={handleSwNotify} style={{ background: 'transparent', border: '1px solid #b070e0', color: '#b070e0', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      SW Notify
                    </button>
                    <button onClick={handleCheckBeacons} style={{ background: 'transparent', border: '1px solid #e0a040', color: '#e0a040', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      Check Beacons
                    </button>
                  </div>
                  <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={handleNuclearReset} style={{ background: '#D42B2B', border: 'none', color: '#fff', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      Nuclear Reset
                    </button>
                    <button onClick={refreshPushStatus} style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.3)', color: '#94A3B8', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.6rem' }}>
                      Refresh
                    </button>
                  </div>
                  <div style={{ marginTop: '0.3rem', fontSize: '0.55rem', color: '#64748B' }}>
                    Check chrome://gcm-internals for FCM connection status
                  </div>
                </div>
              )}

            <div style={{ position: 'relative' }}>
              <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.3)', color: '#D42B2B', padding: '0.6rem 0.8rem', cursor: 'pointer', fontSize: '1.2rem', position: 'relative' }}
                  aria-label="Notifications"
              >
                &#128276;
                {unreadCount > 0 && (
                    <span className="badge bg-danger" style={{ position: 'absolute', top: '-5px', right: '-5px', fontSize: '0.6rem', borderRadius: '50%', padding: '0.3rem 0.5rem' }}>
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                  <div style={{
                    position: 'absolute', right: 0, top: '100%', width: 'min(360px, 90vw)', maxHeight: '400px', overflowY: 'auto',
                    background: '#111827', border: '1px solid rgba(148,163,184,0.25)', zIndex: 1000, marginTop: '0.5rem'
                  }}>
                    <div style={{ padding: '1rem', borderBottom: '1px solid rgba(148,163,184,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ color: '#D42B2B', fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t.admin.notifications.title}</strong>
                      {unreadCount > 0 && (
                          <button onClick={markAllNotificationsRead} style={{ background: 'transparent', border: 'none', color: '#D42B2B', fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline' }}>
                            {t.admin.notifications.markAllRead}
                          </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94A3B8', fontSize: '0.85rem' }}>{t.admin.notifications.empty}</div>
                    ) : (
                        notifications.slice(0, 10).map(notif => {
                          const notifMeta = typeof notif.metadata === 'string' ? JSON.parse(notif.metadata) : notif.metadata;
                          const notifApptId = notifMeta?.appointment_id;
                          return (
                            <div
                                key={notif.id}
                                onClick={async () => {
                                  if (!notif.is_read) {
                                    try { await axios.patch(`${API}/admin/notifications/${notif.id}/read`); loadNotifications(); } catch (e) { /* ignore */ }
                                  }
                                  setShowNotifications(false);
                                  if (notifApptId) {
                                    setActiveTab('appointments');
                                    loadData();
                                    setHighlightedAppointmentId(notifApptId);
                                    setTimeout(() => {
                                      const row = document.getElementById(`appt-${notifApptId}`);
                                      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }, 300);
                                    setTimeout(() => setHighlightedAppointmentId(null), 3300);
                                  }
                                }}
                                style={{
                                  padding: '0.8rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.1)', cursor: 'pointer',
                                  background: notif.is_read ? 'transparent' : 'rgba(148,163,184,0.05)'
                                }}
                            >
                              <div style={{ fontSize: '0.8rem', color: notif.is_read ? '#64748B' : '#E8ECF0', marginBottom: '0.2rem' }}>{notif.message}</div>
                              <div style={{ fontSize: '0.65rem', color: '#475569' }}>{new Date(notif.created_at).toLocaleString()}</div>
                            </div>
                          );
                        })
                    )}
                  </div>
              )}
            </div>
            </div>
          </div>

          {/* Stats Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1px', background: 'rgba(148,163,184,0.1)', marginBottom: '3rem' }}>
            <div style={statCardStyle}>
              <div style={{ fontSize: '2.5rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", marginBottom: '0.5rem' }}>{stats.today}</div>
              <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t.admin.stats.todayAppts}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '2.5rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", marginBottom: '0.5rem' }}>{stats.pending}</div>
              <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t.admin.stats.pending}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '2.5rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", marginBottom: '0.5rem' }}>{stats.customers}</div>
              <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t.admin.stats.totalCustomers}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '2.5rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", marginBottom: '0.5rem' }}>{stats.avgRating}</div>
              <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t.admin.stats.avgRating} ({stats.totalReviews} {t.admin.stats.reviews})</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <ul className="nav nav-pills" style={{ gap: '1px', background: 'rgba(148,163,184,0.1)', marginBottom: '2rem', flexWrap: 'wrap' }}>
            {tabs.map(([key, label]) => (
                <li className="nav-item" key={key}>
                  <button className={`nav-link ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
                    {label}
                  </button>
                </li>
            ))}
          </ul>

          {renderActiveTab()}

          {/* Notes Modal */}
          {noteModal.show && (
              <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setNoteModal({ show: false, notes: '', customer: '' })}>
                <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">{t.admin.viewNotes} — {noteModal.customer}</h5>
                      <button type="button" className="btn-close" aria-label="Close" onClick={() => setNoteModal({ show: false, notes: '', customer: '' })}></button>
                    </div>
                    <div className="modal-body">
                      <p style={{ color: '#94A3B8', fontSize: '0.95rem', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{noteModal.notes}</p>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-outline-primary" onClick={() => setNoteModal({ show: false, notes: '', customer: '' })}>{t.common.close || 'Close'}</button>
                    </div>
                  </div>
                </div>
              </div>
          )}

        </div>

        {/* Toast Container */}
        {toasts.length > 0 && (
            <div style={{
              position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999,
              display: 'flex', flexDirection: 'column-reverse', gap: '0.75rem', maxWidth: '380px'
            }}>
              {toasts.map(toast => (
                  <div
                      key={toast.toastId}
                      className="cjpa-toast"
                      onClick={() => handleToastClick(toast)}
                      style={{
                        background: '#111827',
                        padding: '1rem 1.2rem',
                        cursor: 'pointer',
                        borderRadius: '4px'
                      }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                      <span style={{ color: '#D42B2B', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>
                        {t.admin.notifications.newAppointment}
                      </span>
                      <button
                          onClick={(e) => { e.stopPropagation(); removeToast(toast.toastId); }}
                          style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: 0 }}
                          aria-label="Dismiss"
                      >
                        &times;
                      </button>
                    </div>
                    <div style={{ color: '#E8ECF0', fontSize: '0.88rem', marginBottom: '0.3rem' }}>
                      {toast.message}
                    </div>
                    <div style={{ color: '#475569', fontSize: '0.7rem' }}>
                      {t.admin.notifications.toastClickHint}
                    </div>
                  </div>
              ))}
            </div>
        )}
      </div>
  );
}
